import * as React from 'react';
import { createPortal } from 'react-dom';
import { shallowEqual, useSelector } from 'react-redux';
import Flex from 'antd/lib/flex';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import { AppstoreOutlined, SendOutlined } from '@ant-design/icons';
import { RootState } from '@/reducers/reducers';
import { selectApp, selectDestinationInfoByDestinationStepConfigId } from '@/selectors/selectors';
import { displayStringForDestination } from '@/helpers/displayHelpers';
import { useMdmPlatformOverrideResolver } from '@/helpers/temporaryMdmDisplayOverride';
import { mapReleaseCalendarColorToAntColor } from '@/helpers';
import { ReleaseStatus } from '@/types';
import { Destination } from '@/types/destinations';
import { DestinationIcon, NavigationSegmented } from '@/components/sidebar/shared/components';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import WorkflowsPlatformIcon from '@/components/icons/WorkflowsPlatformIcon';
import { computeCyclingColorsByKey, computeDestinationColorsByDestinationId } from './releaseCalendarGrouping';

export enum ReleaseCalendarFilterTab {
    Flightpaths = 'flightpaths',
    Destinations = 'destinations',
}

interface ReleaseCalendarFilterBarProps {
    appIds: string[];
    activeTab: ReleaseCalendarFilterTab;
    onTabChange: (tab: ReleaseCalendarFilterTab) => void;
    selectedDestinationIds: string[];
    onToggleDestinationSelected: (destinationId: string) => void;
}

interface FilterLegendProps {
    interactive?: boolean;
    selected?: boolean;
    onToggle?: () => void;
    mainColor?: string;
    secondaryColor?: string;
}

const FilterLegend: React.FC<React.PropsWithChildren<FilterLegendProps>> = ({
    interactive = true,
    selected = false,
    onToggle,
    mainColor,
    secondaryColor,
    children,
}) => (
    <Tag
        closable={interactive && selected}
        onClose={(e) => {
            e.preventDefault();
            onToggle?.();
        }}
        onClick={interactive && !selected ? onToggle : undefined}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            margin: 0,
            borderColor: selected && mainColor ? mainColor : antGrey.grey5,
            backgroundColor: selected && secondaryColor ? secondaryColor : runwayColors.white,
            borderRadius: 8,
            padding: '6px 12px',
            cursor: interactive && !selected ? 'pointer' : 'default',
        }}
    >
        {children}
    </Tag>
);

const FilterLegendColorSwatch: React.FC<{ mainColor: string; secondaryColor: string }> = ({
    mainColor,
    secondaryColor,
}) => (
    <div
        style={{
            width: 63,
            height: 24,
            borderBottomLeftRadius: 44,
            borderBottomRightRadius: 24,
            borderTopRightRadius: 24,
            backgroundColor: secondaryColor,
            border: `1px solid ${mainColor}`,
            flexShrink: 0,
        }}
    />
);

const LEGEND_ROW_GAP = 8;
const LEGEND_OVERLAY_PADDING = 8;

const CollapsibleLegendRows: React.FC<React.PropsWithChildren<{ maxRows?: number }>> = ({ maxRows = 2, children }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [collapsedMaxHeight, setCollapsedMaxHeight] = React.useState<number | undefined>(undefined);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const [rect, setRect] = React.useState<DOMRect | null>(null);
    const [overlayVisible, setOverlayVisible] = React.useState(false);

    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return undefined;
        }
        const measure = () => {
            const firstChild = el.firstElementChild as HTMLElement | null;
            const rowHeight = firstChild?.offsetHeight ?? 0;
            if (rowHeight <= 0) {
                return;
            }
            const maxHeight = rowHeight * maxRows + LEGEND_ROW_GAP * (maxRows - 1);
            setCollapsedMaxHeight(maxHeight);
            setIsOverflowing(el.scrollHeight > maxHeight + 1);
        };
        measure();
        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(el);
        return () => resizeObserver.disconnect();
    }, [children, maxRows]);

    const openOverlay = () => {
        if (isOverflowing && containerRef.current) {
            setRect(containerRef.current.getBoundingClientRect());
        }
    };
    const closeOverlay = () => {
        setOverlayVisible(false);
        window.setTimeout(() => setRect(null), 150);
    };

    React.useEffect(() => {
        if (!rect) {
            return undefined;
        }
        const id = requestAnimationFrame(() => setOverlayVisible(true));
        return () => cancelAnimationFrame(id);
    }, [rect]);

    React.useEffect(() => {
        if (!rect) {
            return undefined;
        }
        const hardClose = () => {
            setOverlayVisible(false);
            setRect(null);
        };
        window.addEventListener('scroll', hardClose, true);
        window.addEventListener('resize', hardClose);
        return () => {
            window.removeEventListener('scroll', hardClose, true);
            window.removeEventListener('resize', hardClose);
        };
    }, [rect]);

    const showFade = isOverflowing && !rect;
    const fadeMask = 'linear-gradient(to bottom, black 40%, transparent 100%)';

    return (
        <div style={{ position: 'relative', width: '100%' }} onMouseEnter={openOverlay}>
            <div
                ref={containerRef}
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: LEGEND_ROW_GAP,
                    width: '100%',
                    maxHeight: collapsedMaxHeight,
                    overflow: 'hidden',
                    maskImage: showFade ? fadeMask : undefined,
                    WebkitMaskImage: showFade ? fadeMask : undefined,
                }}
            >
                {children}
            </div>
            {rect &&
                createPortal(
                    <div
                        onMouseLeave={closeOverlay}
                        style={{
                            position: 'fixed',
                            boxSizing: 'border-box',
                            top: rect.top - LEGEND_OVERLAY_PADDING,
                            left: rect.left - LEGEND_OVERLAY_PADDING,
                            width: rect.width + LEGEND_OVERLAY_PADDING * 2,
                            zIndex: 1001,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: LEGEND_ROW_GAP,
                            padding: LEGEND_OVERLAY_PADDING,
                            backgroundColor: runwayColors.white,
                            border: `1px solid ${antGrey.grey4}`,
                            borderRadius: 8,
                            boxShadow: 'var(--rw-shadow-sm)',
                            opacity: overlayVisible ? 1 : 0,
                            transition: 'opacity 0.15s ease-in-out',
                        }}
                    >
                        {children}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

const destinationIdForDestination = (destination: { destinationId?: string; stepConfigId: string }): string =>
    destination.destinationId ?? `dest-${destination.stepConfigId}`;

export const ReleaseCalendarFilterBar: React.FC<ReleaseCalendarFilterBarProps> = ({
    appIds,
    activeTab,
    onTabChange,
    selectedDestinationIds,
    onToggleDestinationSelected,
}) => {
    const apps = useSelector((state: RootState) => appIds.map((appId) => selectApp(state, appId)), shallowEqual);
    const destinationsByApp = useSelector(
        (state: RootState): Record<string, Destination>[] =>
            appIds.map((appId) => selectDestinationInfoByDestinationStepConfigId(state, appId)),
        shallowEqual,
    );

    const destinations = React.useMemo(
        () =>
            destinationsByApp.reduce(
                (all: Destination[], destinationInfoById: Record<string, Destination>, index: number) =>
                    all.concat(
                        Object.values(destinationInfoById).map((destination) => ({
                            ...destination,
                            appId: destination.appId ?? appIds[index],
                        })),
                    ),
                [] as Destination[],
            ),
        [destinationsByApp, appIds],
    );

    const resolveMdmOverride = useMdmPlatformOverrideResolver();

    const destinationColorEnumById = React.useMemo(
        () => computeDestinationColorsByDestinationId(destinations.map(destinationIdForDestination)),
        [destinations],
    );

    const flightpathColorEnumByAppId = React.useMemo(() => computeCyclingColorsByKey(appIds), [appIds]);

    const hasMultipleFlightpaths = appIds.length > 1;
    const hasMultipleDestinations = destinations.length > 1;

    if (!hasMultipleFlightpaths && !hasMultipleDestinations) {
        return null;
    }

    const showFlightpathsLegend = activeTab === ReleaseCalendarFilterTab.Flightpaths && hasMultipleFlightpaths;
    const showDestinationsLegend = activeTab === ReleaseCalendarFilterTab.Destinations && hasMultipleDestinations;
    const hasLegendContent = showFlightpathsLegend || showDestinationsLegend;

    return (
        <Flex align="center" justify={hasLegendContent ? 'space-between' : 'flex-end'} style={{ marginBottom: 16 }}>
            <Flex align="center" gap={8} wrap style={{ flex: 1, minWidth: 0, marginRight: 16 }}>
                {showFlightpathsLegend &&
                    appIds.map((appId, index) => {
                        const app = apps[index];
                        const colorResult = mapReleaseCalendarColorToAntColor(
                            flightpathColorEnumByAppId[appId],
                            ReleaseStatus.Active,
                        );
                        return (
                            <FilterLegend
                                key={appId}
                                interactive={false}
                                selected
                                mainColor={colorResult ? colorResult[0] : undefined}
                                secondaryColor={colorResult ? colorResult[1] : undefined}
                            >
                                {colorResult && (
                                    <FilterLegendColorSwatch
                                        mainColor={colorResult[0]}
                                        secondaryColor={colorResult[1]}
                                    />
                                )}
                                <WorkflowsPlatformIcon platform={app.appSettings.platform} width={24} height={24} />
                                <Text style={{ color: antGrey.grey8 }}>{app.appSettings.appName}</Text>
                            </FilterLegend>
                        );
                    })}
                {showDestinationsLegend && (
                    <CollapsibleLegendRows maxRows={2}>
                        {destinations.map((destination: Destination) => {
                            const destinationId = destinationIdForDestination(destination);
                            const colorResult = mapReleaseCalendarColorToAntColor(
                                destinationColorEnumById[destinationId],
                                ReleaseStatus.Active,
                            );
                            return (
                                <FilterLegend
                                    key={destination.stepConfigId}
                                    selected={selectedDestinationIds.includes(destinationId)}
                                    onToggle={() => onToggleDestinationSelected(destinationId)}
                                    mainColor={colorResult ? colorResult[0] : undefined}
                                    secondaryColor={colorResult ? colorResult[1] : undefined}
                                >
                                    {colorResult && (
                                        <FilterLegendColorSwatch
                                            mainColor={colorResult[0]}
                                            secondaryColor={colorResult[1]}
                                        />
                                    )}
                                    <DestinationIcon destination={destination} size={24} />
                                    <Text style={{ color: antGrey.grey8 }}>
                                        {displayStringForDestination(
                                            destination.platform,
                                            destination.appStoreType,
                                            destination.name,
                                            destination.appName,
                                            resolveMdmOverride(destination.appId)?.label,
                                        )}
                                    </Text>
                                </FilterLegend>
                            );
                        })}
                    </CollapsibleLegendRows>
                )}
            </Flex>
            <NavigationSegmented
                value={activeTab}
                onChange={(value) => onTabChange(value as ReleaseCalendarFilterTab)}
                style={{ width: 290 }}
                options={[
                    {
                        value: ReleaseCalendarFilterTab.Flightpaths,
                        icon: <SendOutlined />,
                        label: ReleaseCalendarFilterTab.Flightpaths,
                    },
                    {
                        value: ReleaseCalendarFilterTab.Destinations,
                        icon: <AppstoreOutlined />,
                        label: ReleaseCalendarFilterTab.Destinations,
                    },
                ]}
            />
        </Flex>
    );
};

export default ReleaseCalendarFilterBar;
